import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBundles } from '../hooks/useBundles';
import { BundleCard } from '../components/BundleCard';
import type { BundleSummary } from '../lib/catalogTypes';

// ─── Animated counter ────────────────────────────────────────────────────────

function AnimatedNumber({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        let start = 0;
        const step = target / 40;
        const id = setInterval(() => {
          start = Math.min(start + step, target);
          setValue(Math.round(start));
          if (start >= target) clearInterval(id);
        }, 30);
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  );
}

// ─── Filter bar ───────────────────────────────────────────────────────────────

type SortKey = 'newest' | 'discount' | 'popularity';
type TypeKey = '' | 'fixed' | 'byob' | 'mix_match' | 'frame_print';
type AvailKey = '' | 'featured' | 'ending_soon';

const TYPE_OPTIONS: { value: TypeKey; label: string }[] = [
  { value: '',           label: 'All Types' },
  { value: 'fixed',      label: 'Fixed Bundle' },
  { value: 'byob',       label: 'Build Your Own' },
  { value: 'mix_match',  label: 'Mix & Match' },
  { value: 'frame_print',label: 'Frame + Print' },
];

function FilterBar({
  search, onSearch,
  sort, onSort,
  typeFilter, onType,
  availFilter, onAvail,
  total,
}: {
  search: string; onSearch: (v: string) => void;
  sort: SortKey; onSort: (v: SortKey) => void;
  typeFilter: TypeKey; onType: (v: TypeKey) => void;
  availFilter: AvailKey; onAvail: (v: AvailKey) => void;
  total: number;
}) {
  return (
    <div
      className="sticky top-0 z-30 border-b border-[#EDE9E2] bg-white/95 backdrop-blur-md"
      role="search"
      aria-label="Filter bundles"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center gap-3 py-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[160px]">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone"
              fill="none" viewBox="0 0 16 16" aria-hidden="true"
            >
              <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              placeholder="Search bundles…"
              value={search}
              onChange={e => onSearch(e.target.value)}
              className="w-full rounded-full border border-[#DDD8D2] bg-[#F9F7F4] py-2 pl-9 pr-4 text-sm text-ink placeholder-stone outline-none transition-colors focus:border-[#C9A84C] focus:bg-white"
              aria-label="Search bundles"
            />
          </div>

          {/* Sort */}
          <select
            value={sort}
            onChange={e => onSort(e.target.value as SortKey)}
            className="rounded-full border border-[#DDD8D2] bg-white py-2 pl-3 pr-8 text-sm text-ink outline-none focus:border-[#C9A84C]"
            aria-label="Sort by"
          >
            <option value="newest">Newest</option>
            <option value="discount">Highest Discount</option>
            <option value="popularity">Popularity</option>
          </select>

          {/* Type */}
          <select
            value={typeFilter}
            onChange={e => onType(e.target.value as TypeKey)}
            className="rounded-full border border-[#DDD8D2] bg-white py-2 pl-3 pr-8 text-sm text-ink outline-none focus:border-[#C9A84C]"
            aria-label="Bundle type"
          >
            {TYPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {/* Availability pills */}
          <div className="flex items-center gap-2" role="group" aria-label="Availability">
            {([
              { value: 'featured',     label: '★ Featured' },
              { value: 'ending_soon',  label: '⏰ Ending Soon' },
            ] as { value: AvailKey; label: string }[]).map(o => (
              <button
                key={o.value}
                type="button"
                onClick={() => onAvail(availFilter === o.value ? '' : o.value)}
                className="rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-150"
                style={
                  availFilter === o.value
                    ? { background: '#C9A84C', borderColor: '#C9A84C', color: '#fff' }
                    : { borderColor: '#DDD8D2', color: '#8A7A6B' }
                }
                aria-pressed={availFilter === o.value}
              >
                {o.label}
              </button>
            ))}
          </div>

          {/* Result count */}
          <span className="ml-auto text-xs text-stone">{total} {total === 1 ? 'bundle' : 'bundles'}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div
      className="overflow-hidden rounded-2xl bg-white"
      style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}
      aria-hidden="true"
    >
      <div className="aspect-[4/3] w-full" style={{ background: 'linear-gradient(90deg, #EDE9E2 25%, #F5F2EE 50%, #EDE9E2 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
      <div className="p-5 space-y-3">
        <div className="h-3 w-1/3 rounded-full" style={{ background: '#EDE9E2' }} />
        <div className="h-5 w-3/4 rounded-full" style={{ background: '#EDE9E2' }} />
        <div className="h-3 w-full rounded-full" style={{ background: '#EDE9E2' }} />
        <div className="h-3 w-2/3 rounded-full" style={{ background: '#EDE9E2' }} />
        <div className="mt-4 flex justify-between pt-4 border-t border-[#F0ECE6]">
          <div className="h-3 w-1/4 rounded-full" style={{ background: '#EDE9E2' }} />
          <div className="h-3 w-1/4 rounded-full" style={{ background: '#EDE9E2' }} />
        </div>
      </div>
      <style>{`@keyframes shimmer { from { background-position: -200% 0 } to { background-position: 200% 0 } }`}</style>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ hasFilters, onClear }: { hasFilters: boolean; onClear: () => void }) {
  return (
    <div className="flex flex-col items-center py-32 text-center" role="status">
      {/* Luxury illustration */}
      <svg className="mb-6 h-24 w-24 text-[#DDD8D2]" fill="none" viewBox="0 0 96 96" aria-hidden="true">
        <rect x="12" y="12" width="30" height="30" rx="4" stroke="currentColor" strokeWidth="1.5" />
        <rect x="54" y="12" width="30" height="30" rx="4" stroke="currentColor" strokeWidth="1.5" />
        <rect x="12" y="54" width="30" height="30" rx="4" stroke="currentColor" strokeWidth="1.5" />
        <rect x="54" y="54" width="30" height="30" rx="4" stroke="currentColor" strokeWidth="1.5" />
        <path d="M32 48h32M48 32v32" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="4 4" />
      </svg>
      <h3 className="font-serif text-2xl font-light text-ink">
        {hasFilters ? 'No bundles match your filters' : 'No bundles available right now'}
      </h3>
      <p className="mt-2 max-w-xs text-sm text-stone">
        {hasFilters
          ? "Try adjusting your search or filters to find what you're looking for."
          : 'Check back soon — we regularly add new curated collections.'}
      </p>
      <div className="mt-8 flex gap-3">
        {hasFilters && (
          <button
            onClick={onClear}
            className="rounded-full border border-[#DDD8D2] px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:border-ink"
          >
            Clear filters
          </button>
        )}
        <Link
          to="/shop"
          className="rounded-full px-5 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
          style={{ background: '#1A1612' }}
        >
          Browse Products
        </Link>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function isEndingSoon(endsAt: string | null) {
  if (!endsAt) return false;
  return new Date(endsAt).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000;
}

function sortBundles(bundles: BundleSummary[], sort: SortKey): BundleSummary[] {
  return [...bundles].sort((a, b) => {
    if (sort === 'discount') {
      const da = a.discount_type === 'percentage' ? Number(a.discount_value) : 0;
      const db = b.discount_type === 'percentage' ? Number(b.discount_value) : 0;
      return db - da;
    }
    if (sort === 'popularity') return b.priority - a.priority;
    // newest: by id desc
    return b.id - a.id;
  });
}

export function BundlesPage() {
  const { data: bundles, isLoading, isError } = useBundles();

  const [search, setSearch]         = useState('');
  const [sort, setSort]             = useState<SortKey>('newest');
  const [typeFilter, setTypeFilter] = useState<TypeKey>('');
  const [availFilter, setAvailFilter] = useState<AvailKey>('');

  const hasFilters = !!(search || typeFilter || availFilter);

  const filtered = useMemo(() => {
    if (!bundles) return [];
    let list = bundles.filter(b => {
      if (search) {
        const q = search.toLowerCase();
        if (!b.title.toLowerCase().includes(q) && !(b.description ?? '').toLowerCase().includes(q)) return false;
      }
      if (typeFilter && (b as BundleSummary & { type?: string }).type !== typeFilter) return false;
      if (availFilter === 'featured' && !b.is_featured) return false;
      if (availFilter === 'ending_soon' && !isEndingSoon(b.ends_at)) return false;
      return true;
    });
    return sortBundles(list, sort);
  }, [bundles, search, sort, typeFilter, availFilter]);

  const featured = filtered.filter(b => b.is_featured);
  const regular  = filtered.filter(b => !b.is_featured);

  // Stats
  const stats = useMemo(() => {
    if (!bundles?.length) return null;
    const pctBundles = bundles.filter(b => b.discount_type === 'percentage');
    const avg = pctBundles.length
      ? Math.round(pctBundles.reduce((s, b) => s + Number(b.discount_value), 0) / pctBundles.length)
      : null;
    const top = [...bundles].sort((a, b) => b.priority - a.priority)[0];
    return { count: bundles.length, avgDiscount: avg, topTitle: top?.title ?? null };
  }, [bundles]);

  function clearFilters() {
    setSearch(''); setSort('newest'); setTypeFilter(''); setAvailFilter('');
  }

  return (
    <div className="min-h-screen" style={{ background: '#F9F7F4' }}>

      {/* ── Hero ──────────────────────────────────────────────── */}
      <div className="bg-white border-b border-[#EDE9E2]">
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-14 sm:px-6 lg:px-8 md:pb-16 md:pt-20">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.22em] text-stone">
            Bundles &amp; Sets
          </p>
          <h1 className="max-w-xl font-serif text-4xl font-light leading-tight text-ink md:text-5xl lg:text-6xl">
            Save More With<br />
            <em className="not-italic" style={{ color: '#C9A84C' }}>Curated Collections</em>
          </h1>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-stone">
            Thoughtfully paired prints, frames, and décor — priced to give you more for less.
            Every bundle is hand-curated for style, quality, and value.
          </p>

          {/* Stats row */}
          {stats && (
            <div className="mt-10 flex flex-wrap gap-8 border-t border-[#EDE9E2] pt-8">
              <div>
                <p className="font-serif text-3xl font-light text-ink">
                  <AnimatedNumber target={stats.count} />
                </p>
                <p className="mt-1 text-xs font-medium uppercase tracking-wide text-stone">Active Bundles</p>
              </div>
              {stats.avgDiscount !== null && (
                <div>
                  <p className="font-serif text-3xl font-light" style={{ color: '#C9A84C' }}>
                    <AnimatedNumber target={stats.avgDiscount} suffix="%" />
                  </p>
                  <p className="mt-1 text-xs font-medium uppercase tracking-wide text-stone">Average Savings</p>
                </div>
              )}
              {stats.topTitle && (
                <div className="hidden sm:block">
                  <p className="font-serif text-lg font-light text-ink line-clamp-1 max-w-[200px]">
                    {stats.topTitle}
                  </p>
                  <p className="mt-1 text-xs font-medium uppercase tracking-wide text-stone">Most Popular</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Filters ───────────────────────────────────────────── */}
      <FilterBar
        search={search} onSearch={setSearch}
        sort={sort} onSort={setSort}
        typeFilter={typeFilter} onType={setTypeFilter}
        availFilter={availFilter} onAvail={setAvailFilter}
        total={filtered.length}
      />

      {/* ── Content ───────────────────────────────────────────── */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">

        {/* Loading skeletons */}
        {isLoading && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="rounded-2xl bg-red-50 p-8 text-center text-sm text-red-600" role="alert">
            Failed to load bundles.{' '}
            <button className="underline" onClick={() => window.location.reload()}>Try again</button>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !isError && filtered.length === 0 && (
          <EmptyState hasFilters={hasFilters} onClear={clearFilters} />
        )}

        {/* Featured 2-col */}
        {featured.length > 0 && (
          <section className="mb-14" aria-label="Featured bundles">
            <div className="mb-6 flex items-center gap-3">
              <span
                className="h-px flex-1"
                style={{ background: 'linear-gradient(to right, #C9A84C, transparent)' }}
              />
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-stone">
                ★ Featured
              </p>
              <span
                className="h-px flex-1"
                style={{ background: 'linear-gradient(to left, #C9A84C, transparent)' }}
              />
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              {featured.map(b => <BundleCard key={b.id} bundle={b} />)}
            </div>
          </section>
        )}

        {/* Regular grid */}
        {regular.length > 0 && (
          <section aria-label="All bundles">
            {featured.length > 0 && (
              <p className="mb-6 text-[11px] font-medium uppercase tracking-[0.2em] text-stone">
                All Bundles
              </p>
            )}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {regular.map(b => <BundleCard key={b.id} bundle={b} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
